import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Sparkles, ExternalLink } from "@mynaui/icons-react";

const getSeverityColor = (severity) => {
  switch (severity) {
    case "High":
    case "Critical":
      return "#C10007";
    case "Medium":
      return "#BB4D00";
    case "Low":
      return "#22C55E";
    default:
      return "#6B7280";
  }
};

const getSeverityBgColor = (severity) => {
  switch (severity) {
    case "High":
    case "Critical":
      return "#FFC9C9";
    case "Medium":
      return "#FEE685";
    case "Low":
      return "#F0FDF4";
    default:
      return "#F3F4F6";
  }
};

const AiDrivenInsights = ({ insights, maxHeight }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  React.useEffect(() => {
    if (insights.length > 0 && expanded === false) {
      const firstExpanded = insights.find((i) => i.expanded);
      setExpanded(firstExpanded ? firstExpanded.id : insights[0]?.id || false);
    }
  }, [insights]);

  const handleChange = (panelId) => (_, isExpanded) => {
    setExpanded(isExpanded ? panelId : false);
  };

  return (
    <Card
      sx={{
        height: maxHeight || "auto",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <CardContent
        sx={{
          p: 3,
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2,
            flexShrink: 0,
          }}
        >
          <Sparkles size={24} color="#6B7280" strokeWidth={1.5} />
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: "18px",
              lineHeight: "28px",
              color: "#111827",
            }}
          >
            AI-Driven Insights
          </Typography>
        </Box>

        <Box
          sx={{
            overflowY: "auto",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            pr: 2,
            "&::-webkit-scrollbar": { width: "2px" },
            "&::-webkit-scrollbar-thumb": {
              background: "#94A3B8",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            scrollbarWidth: "thin",
            scrollbarColor: "#94A3B8 transparent",
          }}
        >
          {insights.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                flex: 1,
                gap: 1.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#6B7280",
                  lineHeight: "20px",
                }}
              >
                No insights for the day
              </Typography>
            </Box>
          ) : insights.map((insight) => (
            <Accordion
              key={insight.id}
              expanded={expanded === insight.id}
              onChange={handleChange(insight.id)}
              disableGutters
              elevation={0}
              sx={{
                "&:before": { display: "none" },
                borderBottom: "1px solid #C6D2FF",
                "&:last-of-type": { borderBottom: "none" },
                background: "transparent",
                pt: 2,
                "&:first-of-type": { pt: 0 },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  px: 0,
                  minHeight: 48,
                  "& .MuiAccordionSummary-content": { my: 1.5 },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: "16px",
                      lineHeight: "24px",
                      color: "#020618",
                    }}
                  >
                    {insight.title}
                  </Typography>
                  {insight.severity && (
                    <Chip
                      label={insight.severity}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor:
                          insight.severityColor ||
                          getSeverityColor(insight.severity),
                        color:
                          insight.severityColor ||
                          getSeverityColor(insight.severity),
                        bgcolor: getSeverityBgColor(insight.severity),
                        fontWeight: 600,
                        fontSize: "12px",
                        lineHeight: "16px",
                        borderRadius: "8px",
                        padding: "2px 8px",
                        height: "auto",
                      }}
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 0, pt: 0, pb: 2 }}>
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: "14px",
                    lineHeight: "20px",
                    color: "#6B7280",
                    mb: 2,
                  }}
                >
                  {insight.description}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    href="#"
                    underline="none"
                    sx={{
                      color: "#22C55E",
                      fontSize: "14px",
                      fontWeight: 400,
                      lineHeight: "20px",
                    }}
                  >
                    {insight.recommendation}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: "14px",
                        lineHeight: "20px",
                        color: "#020618",
                      }}
                    >
                      Asset: {insight.asset}
                    </Typography>
                    <ExternalLink
                      size={20}
                      color="#020618"
                      strokeWidth={1.5}
                      style={{ cursor: "pointer" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/assets/${insight.assetId}`);
                      }}
                    />
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

      </CardContent>
    </Card>
  );
};

export default AiDrivenInsights;
